/**
 * CodeMirror Intelligence — Engine-backed language features.
 *
 * Provides a single `magicMDIntelligence()` factory that returns CodeMirror
 * extensions for completion, hover, and diagnostics, all backed by the
 * Engine's intelligence/* JSON-RPC methods.
 *
 * Usage:
 * ```ts
 * import { magicMDIntelligence } from "../lib/codemirror-intelligence";
 *
 * const extensions = [
 *   ...magicMDIntelligence({
 *     getEngine: () => getPlatformService().engine,
 *     getSessionId: () => usePluginExecutionStore.getState().sessionId,
 *   }),
 * ];
 * ```
 */

import type { Extension } from "@codemirror/state";
import { intelligenceActions } from "./actions";
import { intelligenceCompletion } from "./completion";
import { intelligenceDiagnostics } from "./diagnostics";
import { intelligenceHover } from "./hover";
import { intelligenceInlayHints } from "./inlay-hints";
import { intelligencePreviewHighlight } from "./preview-highlight";
import { intelligenceSemanticTokens } from "./semantic-tokens";
import type { IntelligenceConfig } from "./types";

export type { IntelligenceConfig } from "./types";
export {
    intelligencePreviewField,
    setIntelligencePreviewRange,
} from "./preview-highlight";

/**
 * Create a set of CodeMirror extensions backed by Engine intelligence.
 *
 * Returns an array of extensions (completion, hover, diagnostics).
 * Each extension gracefully degrades to no-op when Engine is unavailable.
 */
export function magicMDIntelligence(config: IntelligenceConfig): Extension[] {
    return [
        intelligenceCompletion(config),
        intelligenceHover(config),
        intelligenceDiagnostics(config),
        intelligenceSemanticTokens(config),
        intelligenceInlayHints(config),
        ...intelligencePreviewHighlight(),
        ...intelligenceActions(config),
    ];
}
